import { computed, reactive, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import TaccAsanaAPI from 'dashboard/api/taccAsana';

const toOption = (item, fallback = '') => ({
  value: item.gid || item.value || '',
  label: item.name || item.email || fallback,
  dot: item.color && item.color !== 'none' ? item.color : item.dot,
});

const selectedLabel = (items, value) =>
  items.find(item => String(item.value || '') === String(value || ''))?.label ||
  '';

const formatDate = value => {
  if (!value) return 'No due date';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(new Date(`${value}T00:00:00`));
};

const nextWeekDate = () => {
  const date = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
  return date.toISOString().slice(0, 10);
};

export function useAsanaTaskForm(props, emit) {
  const dialogRef = ref(null);
  const isSaving = ref(false);
  const isLoadingOptions = ref(false);
  const errorMessage = ref('');
  const mode = ref('create');

  const options = reactive({
    workspaces: [],
    projects: [],
    tasks: [],
    users: [],
  });

  const form = reactive({
    workspace_gid: '',
    project_gid: '',
    parent_task_gid: '',
    name: '',
    notes: '',
    assignee_gid: '',
    due_on: '',
    priority: 'medium',
    comment: '',
    completed: false,
  });

  const priorityOptions = [
    { value: 'low', label: 'Low', dot: '#12b76a' },
    { value: 'medium', label: 'Medium', dot: '#f79009' },
    { value: 'high', label: 'High', dot: '#f97316' },
    { value: 'urgent', label: 'Urgent', dot: '#e5484d' },
  ];

  const isManage = computed(() => mode.value === 'manage');
  const isCompleted = computed(() => isManage.value && form.completed);
  const title = computed(() =>
    isManage.value ? 'Manage Asana task' : 'Add to Asana'
  );
  const description = computed(() =>
    isManage.value
      ? 'This conversation is linked to a task in Asana.'
      : 'Create a task or subtask in Asana from this conversation.'
  );

  const workspaceOptions = computed(() => options.workspaces.map(toOption));
  const projectOptions = computed(() => options.projects.map(toOption));
  const taskOptions = computed(() => [
    { value: '', label: 'No parent - create project task' },
    ...options.tasks.map(toOption),
  ]);
  const userOptions = computed(() => [
    { value: '', label: 'Unassigned' },
    ...options.users.map(user => ({
      value: user.gid,
      label: user.name || user.email,
      dot: '#a78bfa',
    })),
  ]);

  const taskName = computed(() => props.task?.name || form.name);
  const taskProjectName = computed(() => props.task?.project?.name || 'Asana');
  const taskPermalink = computed(() => props.task?.permalink_url || '');
  const dueLabel = computed(() => formatDate(form.due_on));

  const resetForm = task => {
    form.workspace_gid = task?.workspace?.gid || '';
    form.project_gid = task?.project?.gid || '';
    form.parent_task_gid = task?.parent_task?.gid || '';
    form.name = task?.name || '';
    form.notes = task?.notes || '';
    form.assignee_gid = task?.assignee?.gid || '';
    form.due_on = task?.due_on || nextWeekDate();
    form.priority = task?.priority || 'medium';
    form.comment = '';
    form.completed = !!task?.completed;
  };

  const loadWorkspaces = async () => {
    const { data } = await TaccAsanaAPI.getWorkspaces();
    options.workspaces = data.workspaces || [];
    if (!form.workspace_gid && options.workspaces[0]) {
      form.workspace_gid = options.workspaces[0].gid;
    }
  };

  const loadProjects = async () => {
    options.projects = [];
    options.tasks = [];
    options.users = [];
    if (!form.workspace_gid) return;

    const { data } = await TaccAsanaAPI.getProjects(form.workspace_gid);
    options.projects = data.projects || [];
    if (!form.project_gid && options.projects[0]) {
      form.project_gid = options.projects[0].gid;
    }
  };

  const loadProjectDetails = async () => {
    options.tasks = [];
    options.users = [];
    if (!form.workspace_gid || !form.project_gid) return;

    const [tasksResponse, usersResponse] = await Promise.allSettled([
      TaccAsanaAPI.getTasks(form.project_gid),
      TaccAsanaAPI.getUsers(form.workspace_gid, form.project_gid),
    ]);

    if (tasksResponse.status === 'fulfilled') {
      options.tasks = tasksResponse.value.data.tasks || [];
    }

    if (usersResponse.status === 'fulfilled') {
      options.users = usersResponse.value.data.users || [];
    } else {
      const { data } = await TaccAsanaAPI.getUsers(form.workspace_gid);
      options.users = data.users || [];
    }
  };

  const loadOptions = async () => {
    isLoadingOptions.value = true;
    errorMessage.value = '';
    try {
      await loadWorkspaces();
      await loadProjects();
      await loadProjectDetails();
    } catch (error) {
      errorMessage.value =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Could not load Asana data.';
    } finally {
      isLoadingOptions.value = false;
    }
  };

  const open = (nextMode = 'create', task = null) => {
    mode.value = nextMode;
    resetForm(task);
    dialogRef.value?.open();
    loadOptions();
  };

  const close = () => {
    emit('close');
    dialogRef.value?.close();
  };

  const buildPayload = () => ({
    workspace_gid: form.workspace_gid,
    workspace_name: selectedLabel(workspaceOptions.value, form.workspace_gid),
    project_gid: form.project_gid,
    project_name: selectedLabel(projectOptions.value, form.project_gid),
    parent_task_gid: form.parent_task_gid,
    parent_task_name: selectedLabel(taskOptions.value, form.parent_task_gid),
    name: form.name.trim(),
    notes: form.notes.trim(),
    assignee_gid: form.assignee_gid,
    assignee_name: selectedLabel(userOptions.value, form.assignee_gid),
    due_on: form.due_on,
    priority: form.priority,
    comment: form.comment.trim(),
    completed: form.completed,
  });

  const save = async (completed = form.completed) => {
    errorMessage.value = '';
    if (!isManage.value && !form.name.trim()) {
      errorMessage.value = 'Add a task name.';
      return;
    }

    isSaving.value = true;
    try {
      const payload = { ...buildPayload(), completed };
      const response = isManage.value
        ? await TaccAsanaAPI.updateTask(
            props.conversationId,
            props.task.id,
            payload
          )
        : await TaccAsanaAPI.createTask(props.conversationId, payload);

      emit('saved', response.data.task);
      close();
    } catch (error) {
      errorMessage.value =
        error?.response?.data?.error ||
        error?.response?.data?.errors?.join(', ') ||
        error?.message ||
        'Could not save Asana task.';
    } finally {
      isSaving.value = false;
    }
  };

  const deleteTask = async () => {
    if (
      !window.confirm(
        `Delete "${props.task?.name || 'this task'}" from Asana?\n\nThis cannot be undone.`
      )
    ) {
      return;
    }

    isSaving.value = true;
    try {
      await TaccAsanaAPI.unlinkTask(props.conversationId, props.task.id, true);
      emit('deleted', props.task);
      close();
    } catch (error) {
      useAlert(error?.response?.data?.error || 'Could not delete Asana task.');
    } finally {
      isSaving.value = false;
    }
  };

  watch(
    () => form.workspace_gid,
    async (nextValue, previousValue) => {
      if (!previousValue || nextValue === previousValue) return;
      form.project_gid = '';
      form.parent_task_gid = '';
      form.assignee_gid = '';
      await loadProjects();
      await loadProjectDetails();
    }
  );

  watch(
    () => form.project_gid,
    async (nextValue, previousValue) => {
      if (!previousValue || nextValue === previousValue) return;
      form.parent_task_gid = '';
      form.assignee_gid = '';
      await loadProjectDetails();
    }
  );

  return {
    description,
    dialogRef,
    dueLabel,
    errorMessage,
    form,
    isCompleted,
    isLoadingOptions,
    isManage,
    isSaving,
    open,
    close,
    deleteTask,
    priorityOptions,
    projectOptions,
    save,
    taskName,
    taskOptions,
    taskPermalink,
    taskProjectName,
    title,
    userOptions,
    workspaceOptions,
  };
}
